import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MagnifyingGlass, ArrowRight } from '@phosphor-icons/react';
import API from '../lib/api';
import { optimizeImg } from '../lib/image';
import { catalogCache } from '../lib/catalogCache';

const Shop = () => {
  const [sp] = useSearchParams();
  const categoryId = sp.get('category');
  const [cats, setCats] = useState([]);
  // Instant paint from the cross-page catalog cache (filtered by category below).
  const [products, setProducts] = useState(() => catalogCache.get());
  const [q, setQ] = useState('');

  useEffect(() => {
    API.get('/categories').then(r => setCats(r.data)).catch(() => {});
    API.get('/catalog', { params: { summary: 1, ...(categoryId ? { category_id: categoryId } : {}) } })
      .then(r => {
        setProducts(r.data);
        // Only cache the "all" response (so Landing's grid stays warm).
        if (!categoryId) catalogCache.set(r.data);
      })
      .catch(() => { if (!catalogCache.get()) setProducts([]); });
  }, [categoryId]);

  const category = cats.find(c => c.category_id === categoryId);
  const filtered = useMemo(() => {
    const base = (products || []).filter(p => !categoryId || p.category_id === categoryId);
    if (!q) return base;
    const needle = q.toLowerCase();
    return base.filter(p => p.name.toLowerCase().includes(needle));
  }, [products, q, categoryId]);

  return (
    <div className="p-6">
      <Link to="/categories" className="text-[10px] tracking-[0.24em] uppercase font-bold inline-flex items-center gap-1" data-testid="shop-back"><ArrowLeft size={12} /> All Categories</Link>
      <div className="mt-3 reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— {category?.name || 'Catalog'}</div>
        <h1 className="font-display text-[28px] mt-2 leading-tight">{category?.name || 'All products'}</h1>
        {category?.description && <p className="text-[12px] text-neutral-600 mt-1">{category.description}</p>}
      </div>

      <div className="mt-4 relative">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none z-10" />
        <input value={q} onChange={e => setQ(e.target.value)} className="pk-input !pl-11" placeholder="Search products" data-testid="shop-search" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        {products === null && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="pk-border bg-white brutal-shadow-sm animate-pulse" data-testid={`shop-product-skeleton-${i}`}>
            <div className="aspect-square bg-neutral-200"></div>
            <div className="p-3 border-t-2 border-black space-y-2">
              <div className="h-2 bg-neutral-200 w-14"></div>
              <div className="h-3 bg-neutral-200 w-24"></div>
              <div className="h-3 bg-neutral-200 w-16"></div>
            </div>
          </div>
        ))}
        {filtered.map((p, i) => (
          <Link key={p.product_id} to={`/shop/${p.product_id}`} className="pk-border bg-white brutal-shadow-sm" data-testid={`product-${p.product_id}`}>
            <div className="aspect-square overflow-hidden bg-neutral-100">
              {p.image && (
                <img
                  src={optimizeImg(p.image, 400)}
                  alt={p.name}
                  width="400"
                  height="400"
                  loading={i < 4 ? 'eager' : 'lazy'}
                  fetchpriority={i < 4 ? 'high' : 'auto'}
                  decoding="async"
                  className="w-full h-full object-cover"
                  onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
                  style={{ opacity: 0, transition: 'opacity 200ms ease-out' }}
                />
              )}
            </div>
            <div className="p-3 border-t-2 border-black">
              <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">{p.pricing_mode.replace('_', ' ')}</div>
              <div className="font-bold text-[13px] mt-1 leading-tight">{p.name}</div>
              <div className="mt-1 flex items-center justify-between">
                <div className="font-mono text-[12px]">From <b>₹{p.base_price}</b></div>
                <ArrowRight size={14} weight="bold" />
              </div>
            </div>
          </Link>
        ))}
        {products !== null && filtered.length === 0 && <div className="col-span-2 text-center text-[13px] text-neutral-500 py-8">No products.</div>}
      </div>
    </div>
  );
};

export default Shop;
